// ============================================================
// Announcements.js
// The owner's screen for writing, publishing and retiring announcements.
//
// ⚠️ THE GATE BELOW IS A UI-LEVEL HIDE, NOT SECURITY.
//
// RoleContext.js is currently stubbed out — it hard-codes isAdmin: true for
// every signed-in user — and the repo contains no Firestore rules at all
// (firebase.json configures `hosting` and nothing else), so any authenticated
// session can write to the `announcements` collection directly from a console.
// Checking the email here stops a colleague who clicks into a screen that is
// not theirs. It does not stop anyone who means to get past it. If these ever
// need to be genuinely restricted, that has to happen in Firestore rules.
//
// The address itself lives in data/teamMembers.js, not here, so it sits with
// the rest of the team identities and appears in exactly one place.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { useRole } from '../hooks/useRole';
import { auth } from '../firebase';
import { isOwnerEmail } from '../data/teamMembers';
import {
  fetchAllAnnouncements, createAnnouncement, setAnnouncementActive, relativeTime,
} from '../utils/announcements';

const C_BG = '#F4F8FC';
const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#9AA8BC';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';
const C_RED = '#A32D2D';

const cardStyle = { background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 16, padding: 20 };
const labelStyle = { display: 'block', color: C_TEXT, fontWeight: 600, fontSize: 13, marginBottom: 4 };
const fieldStyle = {
  width: '100%', border: `1px solid ${C_BORDER}`, borderRadius: 10, padding: '8px 12px',
  fontSize: 14, color: C_TEXT, background: C_CARD, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s',
};
const focusOn = (e) => { e.target.style.borderColor = C_ACCENT; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; };
const focusOff = (e) => { e.target.style.borderColor = C_BORDER; e.target.style.boxShadow = 'none'; };
const btnPrimary = {
  background: C_ACCENT, border: 'none', color: '#FFFFFF', padding: '10px 20px', borderRadius: 10,
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const btnPlain = {
  background: C_CARD, border: `1px solid ${C_BORDER}`, color: C_TEXT_2, padding: '8px 14px',
  borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

function Announcements() {
  const navigate = useNavigate();
  const { userName: roleName } = useRole();
  const displayName = roleName || auth.currentUser?.email?.split('@')[0]?.toUpperCase();
  const isOwner = isOwnerEmail(auth.currentUser?.email);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await fetchAllAnnouncements());
    } catch (err) {
      console.error('Error loading announcements:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

  const handlePublish = async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await createAnnouncement({ title, body, author: displayName });
      setTitle('');
      setBody('');
      await load();
    } catch (err) {
      console.error('Error publishing announcement:', err);
      alert('Could not publish. Please try again.');
    }
    setSaving(false);
  };

  // Retiring sets active:false — the record stays so the history of what the
  // team was told survives, and so anyone who already dismissed it keeps that.
  const handleSetActive = async (a, active) => {
    if (!active && !window.confirm(`Remove "${a.title}" from the dashboard? It stays in this list.`)) return;
    try {
      await setAnnouncementActive(a.id, active);
      setList(prev => prev.map(x => (x.id === a.id ? { ...x, active } : x)));
    } catch (err) {
      console.error('Error updating announcement:', err);
    }
  };

  if (!isOwner) {
    return (
      <>
        <Sidebar />
        <div style={{ minHeight: '100vh', background: C_BG, marginLeft: SIDEBAR_W, fontFamily: 'Manrope, sans-serif' }}>
          <div style={{ maxWidth: 640, margin: '0 auto', padding: 24 }}>
            <div style={cardStyle}>
              <h1 style={{ color: C_TEXT, fontSize: 20, fontWeight: 800, margin: '0 0 6px 0' }}>Not available</h1>
              <p style={{ color: C_TEXT_2, fontSize: 14, margin: '0 0 16px 0' }}>
                Announcements are written by the shop owner.
              </p>
              <button onClick={() => navigate('/dashboard')} style={btnPrimary}>← Back to Dashboard</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div style={{ minHeight: '100vh', background: C_BG, marginLeft: SIDEBAR_W, fontFamily: 'Manrope, sans-serif' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>

          <div style={{ marginBottom: 20 }}>
            <h1 style={{ color: C_TEXT, fontSize: 24, fontWeight: 800, margin: '0 0 4px 0' }}>📣 Announcements</h1>
            <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>
              Published announcements appear on everyone&apos;s dashboard, and pop up once for each person.
            </p>
          </div>

          {/* Compose */}
          <div style={{ ...cardStyle, marginBottom: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Title</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                placeholder="Shop drawings now show gate quantity"
                style={fieldStyle}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                rows={5}
                placeholder="What changed, and what the team should do differently."
                style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={handlePublish}
                disabled={saving || !title.trim() || !body.trim()}
                style={{
                  ...btnPrimary,
                  opacity: saving || !title.trim() || !body.trim() ? 0.5 : 1,
                  cursor: saving || !title.trim() || !body.trim() ? 'default' : 'pointer',
                }}
              >
                {saving ? 'Publishing…' : 'Publish'}
              </button>
              <span style={{ color: C_TEXT_3, fontSize: 12 }}>Posted as {displayName}</span>
            </div>
          </div>

          {/* History */}
          <div style={cardStyle}>
            <h2 style={{ color: C_TEXT, fontSize: 15, fontWeight: 700, margin: '0 0 12px 0' }}>
              All announcements
            </h2>
            {loading ? (
              <p style={{ color: C_TEXT_2, fontSize: 14, margin: 0 }}>Loading…</p>
            ) : list.length === 0 ? (
              <p style={{ color: C_TEXT_2, fontSize: 14, margin: 0 }}>Nothing published yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {list.map(a => (
                  <div
                    key={a.id}
                    style={{
                      background: a.active ? C_TINT : '#F7F9FC',
                      border: `1px solid ${C_BORDER}`,
                      borderRadius: 12, padding: 14,
                      opacity: a.active ? 1 : 0.7,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                      <p style={{ color: C_TEXT, fontWeight: 700, fontSize: 14, margin: 0 }}>{a.title}</p>
                      <span style={{ color: C_TEXT_3, fontSize: 11, flexShrink: 0 }}>
                        {a.author}{a.author ? ' · ' : ''}{relativeTime(a.createdAt)}
                      </span>
                    </div>
                    <p style={{ color: C_TEXT_2, fontSize: 13, lineHeight: 1.5, margin: '4px 0 10px 0', whiteSpace: 'pre-wrap' }}>
                      {a.body}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.5, padding: '2px 8px', borderRadius: 4,
                        background: a.active ? '#E6F7EE' : '#F1F4F8',
                        color: a.active ? '#0F6E56' : C_TEXT_3,
                      }}>
                        {a.active ? 'PUBLISHED' : 'REMOVED'}
                      </span>
                      {a.active ? (
                        <button
                          onClick={() => handleSetActive(a, false)}
                          style={{ ...btnPlain, color: C_RED, padding: '6px 12px' }}
                        >
                          Remove from dashboard
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSetActive(a, true)}
                          style={{ ...btnPlain, padding: '6px 12px' }}
                        >
                          Publish again
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

export default Announcements;
